<!-- eslint-disable vue/multi-word-component-names -->
<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { getAllArticles } from "../utils/articles.js";
import Header from "../components/Header.vue";
import HomePopup from "../components/HomePopup.vue";

const router = useRouter();

const articles = ref([]);
const currentYear = new Date().getFullYear();

const IMG = {
  hero: "https://brandintegrated.com/wp-content/uploads/2025/09/slide1.png",
  about: "/images/BIArtboard 4.png",
  strategy: "/images/strategy.jpeg",
  branding: "/images/branding.png",
  implementation: "/images/implementation.jpg",
  measurement: "/images/measurement.jpg",
  investor:
    "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80",
  portfolio:
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80",
};

const featuredArticles = computed(() =>
  articles.value.slice(0, 4).map((a) => ({
    id: String(a.id),
    image: a.image,
    meta: [a.category, a.date].filter(Boolean).join(" · "),
    title: a.title,
    excerpt: "",
    slug: a.slug,
  })),
);

const fallbackHeroArticle = {
  id: "hero-fallback",
  image: IMG.hero,
  title: "We enable brands to power business",
  category: "Strategic brand & business advisory",
  date: "",
  slug: null,
};

const heroSlides = computed(() =>
  articles.value.length > 0 ? articles.value : [fallbackHeroArticle],
);

const currentCarouselIndex = ref(0);

const goToArticle = (slug) => {
  if (slug) router.push(`/article/${slug}`);
};

const prevSlide = () => {
  const slides = heroSlides.value;
  if (slides.length <= 1) return;
  currentCarouselIndex.value =
    (currentCarouselIndex.value - 1 + slides.length) % slides.length;
};

const nextSlide = () => {
  const slides = heroSlides.value;
  if (slides.length <= 1) return;
  currentCarouselIndex.value = (currentCarouselIndex.value + 1) % slides.length;
};

let sectionObserver;
let carouselInterval = null;

onMounted(async () => {
  try {
    const articlesObj = await getAllArticles();
    articles.value = Object.values(articlesObj).map((article) => ({
      id: article.id,
      image: article.image,
      title: article.title,
      date: article.date,
      categories: article.categories,
      category: article.category,
      slug: article.id,
    }));
  } catch {
    /* ignore */
  }

  carouselInterval = setInterval(() => {
    nextSlide();
  }, 6000);

  await nextTick();
  const sections = document.querySelectorAll(".home-section-fade");
  sectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("in-view");
        sectionObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.06, rootMargin: "0px 0px -48px 0px" },
  );
  sections.forEach((el) => sectionObserver.observe(el));
});

onBeforeUnmount(() => {
  if (carouselInterval) clearInterval(carouselInterval);
  sectionObserver?.disconnect();
});
</script>

<template>
  <div
    class="min-h-screen bg-background font-body text-on-background selection:bg-secondary selection:text-on-secondary"
  >
    <HomePopup />
    <Header />

    <section
      id="blogs"
      class="home-section-fade section-fade relative h-screen min-h-[100svh] w-full overflow-hidden bg-primary"
    >
      <div class="relative h-full w-full">
        <div
          v-for="(article, index) in heroSlides"
          :key="article.id"
          class="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          :class="
            index === currentCarouselIndex
              ? 'z-10 opacity-100'
              : 'z-0 opacity-0'
          "
        >
          <div
            class="absolute inset-0 z-10 bg-gradient-to-t from-primary via-primary/40 to-transparent"
          ></div>
          <img
            :alt="article.title"
            class="h-full w-full object-cover"
            :src="article.image"
          />
        </div>

        <div
          class="hero-overlay absolute inset-0 z-20 flex items-center justify-center px-6 md:justify-start md:px-16"
        >
          <div class="w-full max-w-5xl text-center md:text-left">
            <Transition name="hero-article" mode="out-in">
              <div
                v-if="heroSlides[currentCarouselIndex]"
                :key="heroSlides[currentCarouselIndex].id"
                class="reveal-up"
              >
                <div
                  class="mb-4 flex flex-wrap items-center justify-center gap-3 md:justify-start"
                >
                  <span
                    class="rounded-full bg-secondary-fixed px-3 py-0.5 font-label text-[10px] font-semibold uppercase tracking-widest text-on-secondary-fixed sm:text-xs"
                  >
                    {{ heroSlides[currentCarouselIndex].category }}
                  </span>
                </div>
                <h1
                  class="hero-title mb-6 font-display text-3xl leading-[1.12] tracking-tight text-white sm:text-4xl md:mb-7 md:text-5xl md:leading-[1.08] lg:text-6xl lg:leading-[1.05] xl:text-7xl"
                >
                  {{ heroSlides[currentCarouselIndex].title }}
                </h1>
                <button
                  v-if="heroSlides[currentCarouselIndex].slug"
                  type="button"
                  class="rounded-lg bg-white px-8 py-3 font-label text-xs font-bold uppercase tracking-tighter text-primary transition-all hover:bg-secondary-fixed hover:text-on-secondary-fixed sm:text-label-md"
                  @click="goToArticle(heroSlides[currentCarouselIndex].slug)"
                >
                  Read Article
                </button>
              </div>
            </Transition>
          </div>
        </div>

        <div
          v-if="heroSlides.length > 1"
          class="absolute bottom-10 left-0 z-30 flex w-full justify-end px-6 md:bottom-12 md:px-16"
        >
          <div class="flex space-x-4">
            <button
              type="button"
              class="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white transition-all hover:bg-white hover:text-primary sm:h-14 sm:w-14"
              aria-label="Previous slide"
              @click="prevSlide"
            >
              <span class="material-symbols-outlined text-xl sm:text-2xl"
                >arrow_back</span
              >
            </button>
            <button
              type="button"
              class="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white transition-all hover:bg-white hover:text-primary sm:h-14 sm:w-14"
              aria-label="Next slide"
              @click="nextSlide"
            >
              <span class="material-symbols-outlined text-xl sm:text-2xl"
                >arrow_forward</span
              >
            </button>
          </div>
        </div>
      </div>
    </section>

    <section
      id="about"
      class="home-section-fade section-fade section-padding bg-surface"
    >
      <div
        class="page-x mx-auto grid max-w-[1440px] grid-cols-1 items-stretch gap-12 sm:gap-16 lg:grid-cols-2 lg:gap-24"
      >
        <div
          class="relative flex min-h-0 w-full flex-col lg:h-full lg:min-h-[22rem]"
        >
          <div
            class="flex flex-1 items-center justify-center bg-gradient-to-r from-transparent via-white/85 to-white"
          >
            <img
              alt="Brand Integrated"
              class="w-full max-w-full h-auto object-contain object-center grayscale transition-all duration-700 hover:grayscale-0"
              :src="IMG.about"
              decoding="async"
              loading="lazy"
            />
          </div>
          <div
            class="mt-6 w-fit max-w-[14rem] bg-primary p-8 shadow-2xl sm:mt-8 md:absolute md:mt-0 md:-bottom-10 md:-right-10 md:max-w-none md:p-14"
          >
            <p
              class="font-display text-4xl font-bold text-brand-yellow sm:text-5xl"
            >
              20+
            </p>
            <p
              class="font-label text-[10px] uppercase tracking-widest text-secondary-fixed opacity-90 sm:text-label-md"
            >
              Years Experience
            </p>
          </div>
        </div>
        <div>
          <p
            class="font-label mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-secondary sm:mb-6 sm:text-label-md"
          >
            The Strategic Anchor
          </p>
          <h2
            class="font-display mb-6 text-[1.75rem] leading-[1.15] text-primary sm:mb-8 sm:text-4xl md:mb-10 md:text-6xl md:leading-[1.1]"
          >
            We enable brands to power business.
          </h2>
          <div
            class="font-body mb-10 space-y-5 text-base leading-relaxed text-on-surface-variant sm:mb-12 sm:space-y-6 sm:text-body-lg md:mb-16"
          >
            <p>
              Brand Integrated Consulting is an award-winning strategic brand
              and business advisory firm with a footprint across Africa.
            </p>
            <p>
              For nearly two decades, we have been enabling organizations to
              discover just how remarkable they can be.
            </p>
            <p>
              Our team is made up of multi-disciplined professionals working
              together to deliver an exceptional brand experience to
              <span class="font-semibold text-secondary"
                >power your business</span
              >.
            </p>
          </div>
          <div
            class="grid grid-cols-1 gap-6 border-t border-outline-variant/20 pt-8 sm:grid-cols-2 sm:gap-8 sm:pt-12"
          >
            <div>
              <span
                class="font-headline block text-3xl font-bold tracking-tighter text-on-background sm:text-4xl"
                >100+</span
              >
              <span
                class="font-label text-[10px] uppercase tracking-widest text-on-surface-variant"
                >Brand Strategies Delivered</span
              >
            </div>
            <div>
              <span
                class="font-headline block text-3xl font-bold tracking-tighter text-on-background sm:text-4xl"
                >3</span
              >
              <span
                class="font-label text-[10px] uppercase tracking-widest text-on-surface-variant"
                >Industry Awards</span
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      id="approach"
      class="home-section-fade section-fade section-padding overflow-hidden bg-primary"
    >
      <div class="page-x mx-auto mb-10 max-w-[1440px] sm:mb-16 md:mb-20">
        <p
          class="font-label mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-secondary-fixed sm:mb-6 sm:text-label-md sm:tracking-[0.3em]"
        >
          Our Methodology
        </p>
        <h2
          class="font-display text-[1.75rem] leading-tight text-on-primary sm:text-4xl md:text-7xl"
        >
          The Integrated Model
        </h2>
        <div
          class="font-body mt-6 max-w-3xl space-y-3 text-base leading-relaxed text-surface-variant opacity-90 sm:mt-8 sm:space-y-4 sm:text-body-lg md:mt-10"
        >
          <p>
            We believe that in order to deliver results, the entire organization
            must be aligned to the brand.
          </p>
          <p>
            This includes all facets of marketing, organization management,
            employee engagement and overall client acquisition and management
            strategies.
          </p>
          <p>
            To enable this perfect balance, we have over the years developed an
            integrated 3-phased model that ensures your brand and business
            delivers exceptionally on its core objectives.
          </p>
        </div>
      </div>
      <div
        class="approach-scroll relative flex snap-x snap-mandatory flex-nowrap gap-5 overflow-x-auto pb-8 pl-4 pr-4 no-scrollbar scroll-smooth sm:gap-6 md:gap-12 md:pb-12 md:pl-8 md:pr-8 lg:pl-12 lg:pr-12"
      >
        <div
          class="approach-card approach-card-item group snap-start p-8 md:p-16"
        >
          <span
            class="text-stroke-yellow font-display mb-6 block text-5xl font-bold md:mb-10 md:text-7xl"
            >01</span
          >
          <h3
            class="font-headline mb-4 text-xl text-secondary-fixed md:mb-8 md:text-headline-md"
          >
            Interrogation
          </h3>
          <p
            class="approach-card-body font-body mb-6 text-sm leading-relaxed text-surface-variant opacity-80 md:mb-10 md:text-body-md"
          >
            Brand business audit across key areas.
          </p>
          <div
            class="approach-card-bar relative h-[2px] w-full overflow-hidden bg-white/10"
          >
            <div
              class="absolute inset-0 w-1/4 bg-brand-yellow transition-all duration-700 group-hover:w-full"
            ></div>
          </div>
        </div>
        <div
          class="approach-card approach-card-item group snap-start p-8 md:p-16"
        >
          <span
            class="text-stroke-yellow font-display mb-6 block text-5xl font-bold md:mb-10 md:text-7xl"
            >02</span
          >
          <h3
            class="font-headline mb-4 text-xl text-secondary-fixed md:mb-8 md:text-headline-md"
          >
            Integration
          </h3>
          <p
            class="approach-card-body font-body mb-6 text-sm leading-relaxed text-surface-variant opacity-80 md:mb-10 md:text-body-md"
          >
            Integrated strategy formulation taking into account core business
            strategy and market dynamics.
          </p>
          <div
            class="approach-card-bar relative h-[2px] w-full overflow-hidden bg-white/10"
          >
            <div
              class="absolute inset-0 w-1/4 bg-brand-yellow transition-all duration-700 group-hover:w-full"
            ></div>
          </div>
        </div>
        <div
          class="approach-card approach-card-item group snap-start p-8 md:p-16"
        >
          <span
            class="text-stroke-yellow font-display mb-6 block text-5xl font-bold md:mb-10 md:text-7xl"
            >03</span
          >
          <h3
            class="font-headline mb-4 text-xl text-secondary-fixed md:mb-8 md:text-headline-md"
          >
            Implementation
          </h3>
          <p
            class="approach-card-body font-body mb-6 text-sm leading-relaxed text-surface-variant opacity-80 md:mb-10 md:text-body-md"
          >
            Integrated implementation strategy optimizing relevant interventions
            and channels.
          </p>
          <div
            class="approach-card-bar relative h-[2px] w-full overflow-hidden bg-white/10"
          >
            <div
              class="absolute inset-0 w-1/4 bg-brand-yellow transition-all duration-700 group-hover:w-full"
            ></div>
          </div>
        </div>
        <div
          class="approach-card approach-card-item group snap-start p-8 md:p-16"
        >
          <span
            class="text-stroke-yellow font-display mb-6 block text-5xl font-bold md:mb-10 md:text-7xl"
            >∞</span
          >
          <h3
            class="font-headline mb-4 text-xl text-secondary-fixed md:mb-8 md:text-headline-md"
          >
            Continuous Measurement
          </h3>
          <p
            class="approach-card-body font-body mb-6 text-sm leading-relaxed text-surface-variant opacity-80 md:mb-10 md:text-body-md"
          >
            Brand business audit across key areas.
          </p>
          <div
            class="approach-card-bar relative h-[2px] w-full overflow-hidden bg-white/10"
          >
            <div
              class="absolute inset-0 w-1/4 bg-brand-yellow transition-all duration-700 group-hover:w-full"
            ></div>
          </div>
        </div>
      </div>
    </section>

    <section
      id="services"
      class="home-section-fade section-fade section-padding overflow-hidden bg-surface-container-low"
    >
      <div class="page-x mx-auto max-w-[1440px]">
        <div class="mb-12 sm:mb-20 md:mb-40">
          <p
            class="font-label mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-primary sm:mb-6 sm:text-label-md sm:tracking-[0.3em]"
          >
            Expertise
          </p>
          <h2
            class="font-display max-w-5xl text-[1.75rem] leading-tight text-primary sm:text-4xl md:text-[5.25rem] md:leading-none"
          >
            Strategic Precision in Every Layer.
          </h2>
        </div>

        <div class="space-y-20 sm:space-y-32 md:space-y-48">
          <div
            class="flex flex-col items-center gap-8 sm:gap-12 md:flex-row md:gap-32"
          >
            <div class="order-2 w-full md:order-1 md:w-1/2">
              <div class="mb-5 flex items-center gap-4 sm:mb-8 sm:gap-6">
                <span
                  class="font-headline text-5xl font-bold text-brand-yellow sm:text-7xl md:text-8xl"
                  >01</span
                >
                <div class="h-[2px] w-20 bg-brand-yellow/30"></div>
                <span
                  class="material-symbols-outlined text-3xl text-primary sm:text-4xl"
                  >account_tree</span
                >
              </div>
              <h3
                class="font-headline mb-4 text-xl text-primary sm:mb-6 md:mb-8 md:text-headline-lg"
              >
                Strategy
              </h3>
              <p
                class="font-body text-base leading-relaxed text-on-surface-variant sm:text-body-lg"
              >
                We believe that at the back of any influential brand is the
                strategy that outlines its reason for existence and the
                direction the brand is headed. Therefore, we provide strategic
                solutions based on extensive brand and business interrogation.
              </p>
            </div>
            <div class="order-1 w-full md:order-2 md:w-1/2">
              <div class="service-image-frame">
                <img
                  alt="Strategy"
                  class="h-full w-full object-cover grayscale transition-all duration-1000 hover:grayscale-0"
                  :src="IMG.strategy"
                />
              </div>
            </div>
          </div>

          <div
            class="flex flex-col items-center gap-8 sm:gap-12 md:flex-row md:gap-32"
          >
            <div class="w-full md:w-1/2">
              <div class="service-image-frame">
                <img
                  alt="Analytics"
                  class="h-full w-full object-cover grayscale transition-all duration-1000 hover:grayscale-0"
                  :src="IMG.branding"
                />
              </div>
            </div>
            <div class="w-full md:w-1/2">
              <div class="mb-5 flex items-center gap-4 sm:mb-8 sm:gap-6">
                <span
                  class="font-headline text-5xl font-bold text-brand-yellow sm:text-7xl md:text-8xl"
                  >02</span
                >
                <div class="h-[2px] w-20 bg-brand-yellow/30"></div>
                <span
                  class="material-symbols-outlined text-3xl text-primary sm:text-4xl"
                  >monitoring</span
                >
              </div>
              <h3
                class="font-headline mb-4 text-xl text-primary sm:mb-6 md:mb-8 md:text-headline-lg"
              >
                Analytics
              </h3>
              <p
                class="font-body text-base leading-relaxed text-on-surface-variant sm:text-body-lg"
              >
                We help you make informed decisions based on data analytics. Our
                solutions include Brand Monitor Evaluations, Brand Analytics
                Surveys and Customer Brand Satisfaction Surveys.
              </p>
            </div>
          </div>

          <div
            class="flex flex-col items-center gap-8 sm:gap-12 md:flex-row md:gap-32"
          >
            <div class="order-2 w-full md:order-1 md:w-1/2">
              <div class="mb-5 flex items-center gap-4 sm:mb-8 sm:gap-6">
                <span
                  class="font-headline text-5xl font-bold text-brand-yellow sm:text-7xl md:text-8xl"
                  >03</span
                >
                <div class="h-[2px] w-20 bg-brand-yellow/30"></div>
                <span
                  class="material-symbols-outlined text-3xl text-primary sm:text-4xl"
                  >payments</span
                >
              </div>
              <h3
                class="font-headline mb-4 text-xl text-primary sm:mb-6 md:mb-8 md:text-headline-lg"
              >
                Brand Valuation
              </h3>
              <p
                class="font-body text-base leading-relaxed text-on-surface-variant sm:text-body-lg"
              >
                Brand is often the most valuable asset, superior to fixed assets
                and machinery. We are able to assist clients in determining the
                real value of their brands, an exercise that is often crucial in
                mergers and acquisitions where money is often left on the table
                through carrying out only book valuation.
              </p>
            </div>
            <div class="order-1 w-full md:order-2 md:w-1/2">
              <div class="service-image-frame">
                <img
                  alt="Valuation"
                  class="h-full w-full object-cover grayscale transition-all duration-1000 hover:grayscale-0"
                  :src="IMG.implementation"
                />
              </div>
            </div>
          </div>

          <div
            class="flex flex-col items-center gap-8 sm:gap-12 md:flex-row md:gap-32"
          >
            <div class="w-full md:w-1/2">
              <div class="service-image-frame">
                <img
                  alt="Engagement"
                  class="h-full w-full object-cover grayscale transition-all duration-1000 hover:grayscale-0"
                  :src="IMG.measurement"
                />
              </div>
            </div>
            <div class="w-full md:w-1/2">
              <div class="mb-5 flex items-center gap-4 sm:mb-8 sm:gap-6">
                <span
                  class="font-headline text-5xl font-bold text-brand-yellow sm:text-7xl md:text-8xl"
                  >04</span
                >
                <div class="h-[2px] w-20 bg-brand-yellow/30"></div>
                <span
                  class="material-symbols-outlined text-3xl text-primary sm:text-4xl"
                  >group_work</span
                >
              </div>
              <h3
                class="font-headline mb-4 text-xl text-primary sm:mb-6 md:mb-8 md:text-headline-lg"
              >
                Employee Brand Engagement
              </h3>
              <p
                class="font-body text-base leading-relaxed text-on-surface-variant sm:text-body-lg"
              >
                Reduce staff turnover, improve productivity and efficiency by
                building an engaged workforce. We tailor-make employee
                strategies, workshops and campaigns that constantly keep staff
                engaged with your brand.
              </p>
            </div>
          </div>

          <div
            class="flex flex-col items-center gap-8 pb-4 sm:gap-12 md:flex-row md:gap-32 md:pb-0"
          >
            <div class="order-2 w-full md:order-1 md:w-1/2">
              <div class="mb-5 flex items-center gap-4 sm:mb-8 sm:gap-6">
                <span
                  class="font-headline text-5xl font-bold text-brand-yellow sm:text-7xl md:text-8xl"
                  >05</span
                >
                <div class="h-[2px] w-20 bg-brand-yellow/30"></div>
                <span
                  class="material-symbols-outlined text-3xl text-primary sm:text-4xl"
                  >real_estate_agent</span
                >
              </div>
              <h3
                class="font-headline mb-4 text-xl text-primary sm:mb-6 md:mb-8 md:text-headline-lg"
              >
                Brand Investor Relations
              </h3>
              <p
                class="font-body text-base leading-relaxed text-on-surface-variant sm:text-body-lg"
              >
                Brands normally do a great job of appealing to consumers' hearts
                and minds, but often forget that investors also require a
                specific focus. Our specialist tools work to create and position
                the investor brand so the owners may realize the brand's true
                value.
              </p>
            </div>
            <div class="order-1 w-full md:order-2 md:w-1/2">
              <div class="service-image-frame">
                <img
                  alt="Investor Relations"
                  class="h-full w-full object-cover grayscale transition-all duration-1000 hover:grayscale-0"
                  :src="IMG.investor"
                />
              </div>
            </div>
          </div>

          <div
            class="flex flex-col items-center gap-8 pb-4 sm:gap-12 md:flex-row md:gap-32 md:pb-0"
          >
            <div class="w-full md:w-1/2">
              <div class="service-image-frame">
                <img
                  alt="Optimization"
                  class="h-full w-full object-cover grayscale transition-all duration-1000 hover:grayscale-0"
                  :src="IMG.portfolio"
                />
              </div>
            </div>
            <div class="w-full md:w-1/2">
              <div class="mb-5 flex items-center gap-4 sm:mb-8 sm:gap-6">
                <span
                  class="font-headline text-5xl font-bold text-brand-yellow sm:text-7xl md:text-8xl"
                  >06</span
                >
                <div class="h-[2px] w-20 bg-brand-yellow/30"></div>
                <span
                  class="material-symbols-outlined text-3xl text-primary sm:text-4xl"
                  >architecture</span
                >
              </div>
              <h3
                class="font-headline mb-4 text-xl text-primary sm:mb-6 md:mb-8 md:text-headline-lg"
              >
                Brand Business Portfolio Optimization
              </h3>
              <p
                class="font-body text-base leading-relaxed text-on-surface-variant sm:text-body-lg"
              >
                A portfolio of brands is most valuable when well-optimized to
                capture all relevant consumer and market segments. We are able
                to work with clients to determine optimal positioning
                opportunities in a firm's portfolio of brands so as to grow the
                bottom line.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      id="articles"
      class="home-section-fade section-fade section-padding bg-surface"
    >
      <div class="page-x mx-auto max-w-[1440px]">
        <div
          class="mb-10 flex flex-col items-start justify-between gap-4 sm:mb-16 md:mb-24 md:flex-row md:items-end md:gap-8"
        >
          <div>
            <p
              class="font-label mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-primary sm:mb-6 sm:text-label-md sm:tracking-[0.3em]"
            >
              Intellectual Property
            </p>
            <h2
              class="font-display text-[1.75rem] leading-tight text-primary sm:text-4xl md:text-7xl"
            >
              Latest Thinking
            </h2>
          </div>
          <router-link
            to="/articles"
            class="border-b-2 border-primary pb-1 text-[10px] font-bold uppercase tracking-widest text-primary transition-all hover:border-brand-yellow hover:text-brand-yellow sm:pb-2 sm:text-label-md"
            >View All Articles</router-link
          >
        </div>
        <div
          v-if="featuredArticles.length"
          class="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-10 lg:grid-cols-4 lg:gap-6"
        >
          <article
            v-for="item in featuredArticles"
            :key="item.id"
            class="group cursor-pointer"
            @click="goToArticle(item.slug)"
          >
            <div
              class="mb-5 aspect-[16/9] overflow-hidden rounded-xl shadow-lg sm:mb-8 md:mb-10"
            >
              <img
                alt=""
                class="h-full w-full object-cover transition-transform duration-1000 ease-expo group-hover:scale-105"
                :src="item.image"
              />
            </div>
            <span
              class="font-label mb-3 block text-[10px] font-bold uppercase tracking-widest text-secondary sm:mb-4 sm:text-label-md"
              >{{ item.meta }}</span
            >
            <h3
              class="font-headline mb-3 text-lg leading-tight text-primary transition-colors group-hover:text-secondary sm:mb-4 sm:text-xl md:mb-6 md:text-headline-lg"
            >
              {{ item.title }}
            </h3>
            <p
              v-if="item.excerpt"
              class="font-body leading-relaxed text-on-surface-variant"
            >
              {{ item.excerpt }}
            </p>
          </article>
        </div>
        <p v-else class="font-body text-on-surface-variant">
          New articles will appear here when published.
        </p>
      </div>
    </section>

    <section
      id="contact"
      class="home-section-fade section-fade relative overflow-hidden bg-primary section-padding"
    >
      <div
        class="page-x relative z-10 mx-auto max-w-4xl text-center text-on-primary"
      >
        <h2
          class="font-display mb-8 text-[2rem] leading-[1.1] tracking-tight sm:mb-12 sm:text-5xl md:mb-16 md:text-[6.875rem] md:leading-[0.9]"
        >
          Ready to make an impact?
        </h2>
        <p
          class="font-body mx-auto mb-8 max-w-3xl text-base leading-relaxed opacity-90 sm:mb-10 sm:text-body-lg md:mb-12"
        >
          Tell us about your next move. We'll craft a focused plan that bridges
          strategy and execution—fast.
        </p>
        <div
          class="mb-8 flex flex-col items-stretch justify-center gap-4 sm:mb-10 sm:items-center sm:gap-6 md:flex-row md:gap-10"
        >
          <a
            class="inline-flex btn-header-text-yellow-filled w-full md:w-auto"
            href="mailto:info@brandintegrated.com"
            >Start a Project</a
          >
          <a
            class="inline-flex btn-header-text-yellow w-full md:w-auto"
            href="tel:+254204938268"
            >Call Us · +254 20 4938 268</a
          >
        </div>
        <p class="font-body text-sm opacity-90">
          Prefer a brief? Send it over — we respond within one business day.
        </p>
      </div>
    </section>

    <footer
      class="home-section-fade section-fade w-full border-t border-outline-variant bg-surface section-padding"
    >
      <div
        class="page-x mx-auto grid max-w-[1440px] grid-cols-1 gap-10 sm:gap-12 md:grid-cols-2 md:gap-16 lg:grid-cols-4"
      >
        <div class="lg:col-span-1">
          <div class="mb-6 md:mb-8">
            <div
              class="h-12 w-44 overflow-hidden rounded-lg sm:h-14 sm:w-52 md:h-16 md:w-56"
            >
              <img
                src="/images/bi-logo.jpg"
                alt="Brand Integrated"
                class="h-full w-full object-contain object-left"
              />
            </div>
          </div>
          <p
            class="font-body max-w-xs text-sm leading-relaxed text-on-surface-variant"
          >
            The premium strategic advisory for brands that refuse to be ignored.
          </p>
        </div>
        <div>
          <h4
            class="font-label mb-8 text-[10px] font-bold uppercase tracking-[0.2em] text-primary"
          >
            Office
          </h4>
          <p class="font-body text-sm text-on-surface-variant">
            Brand Integrated Consulting LLP<br />Nairobi, Kenya
          </p>
        </div>
        <div>
          <h4
            class="font-label mb-8 text-[10px] font-bold uppercase tracking-[0.2em] text-primary"
          >
            Contact
          </h4>
          <p class="font-body mb-4 text-sm text-on-surface-variant">
            <a
              class="transition-colors hover:text-primary"
              href="mailto:info@brandintegrated.com"
              >info@brandintegrated.com</a
            >
          </p>
          <p class="font-body text-sm text-on-surface-variant">
            Tel: +254 20 4938 268
          </p>
        </div>
        <div>
          <h4
            class="font-label mb-8 text-[10px] font-bold uppercase tracking-[0.2em] text-primary"
          >
            Newsletter
          </h4>
          <div class="flex border-b border-outline-variant/30 py-2">
            <input
              class="font-body w-full border-none bg-transparent text-sm text-on-background placeholder:text-on-surface-variant/70 focus:ring-0"
              placeholder="Email address"
              type="email"
            />
            <button
              type="button"
              class="inline-flex btn-header-yellow shrink-0"
              aria-label="Subscribe"
            >
              <span class="material-symbols-outlined text-[22px]"
                >arrow_forward</span
              >
            </button>
          </div>
        </div>
      </div>
      <div
        class="page-x mx-auto mt-8 max-w-[1440px] border-t border-outline-variant/20 pt-6 sm:mt-12 sm:pt-8 md:mt-20"
      >
        <div class="flex flex-col items-center gap-4 md:hidden">
          <p class="font-label text-center text-xs text-on-surface-variant">
            © {{ currentYear }} Brand Integrated Consulting. All rights
            reserved.
          </p>
          <div class="flex flex-wrap justify-center gap-4">
            <a
              class="font-label text-xs text-on-surface-variant transition-colors hover:text-primary"
              href="#"
              >Privacy Policy</a
            >
            <a
              class="font-label text-xs text-on-surface-variant transition-colors hover:text-primary"
              href="#"
              >Terms of Service</a
            >
            <router-link
              class="font-label text-xs text-on-surface-variant transition-colors hover:text-primary"
              to="/admin/login"
              >Admin Login</router-link
            >
            <a
              class="font-label text-xs text-on-surface-variant transition-colors hover:text-primary"
              href="https://sitati.vercel.app"
              target="_blank"
              rel="noopener noreferrer"
              >Designed and developed by Sitati</a
            >
          </div>
        </div>
        <div class="hidden items-center justify-between md:flex">
          <p class="font-label text-xs text-on-surface-variant">
            © {{ currentYear }} Brand Integrated Consulting. All rights
            reserved.
          </p>
          <div class="flex gap-6">
            <a
              class="font-label text-xs text-on-surface-variant transition-colors hover:text-primary"
              href="#"
              >Privacy Policy</a
            >
            <a
              class="font-label text-xs text-on-surface-variant transition-colors hover:text-primary"
              href="#"
              >Terms of Service</a
            >
            <router-link
              class="font-label text-xs text-on-surface-variant transition-colors hover:text-primary"
              to="/admin/login"
              >Admin Login</router-link
            >
            <a
              class="font-label text-xs text-on-surface-variant transition-colors hover:text-primary"
              href="https://sitati.vercel.app"
              target="_blank"
              rel="noopener noreferrer"
              >Designed and developed by Sitati</a
            >
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.hero-overlay {
  padding-top: 5.75rem;
  padding-bottom: 5.5rem;
}

@media (min-width: 768px) {
  .hero-overlay {
    padding-top: 8.75rem;
    padding-bottom: 6rem;
  }
}

@media (min-width: 1024px) {
  .hero-overlay {
    padding-top: 10rem;
    padding-bottom: 6.5rem;
  }
}

.hero-title {
  text-wrap: balance;
}

.hero-article-enter-active,
.hero-article-leave-active {
  transition: opacity 0.55s ease;
}
.hero-article-enter-from,
.hero-article-leave-to {
  opacity: 0;
}
</style>
